import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { logger } from '../../../services/logger/logger';
import { showErrorToast, showSuccessToast } from '../../../components/common/BaseToaster';
import ProductAddFlowStep1 from '../../../components/delivery/ProductAddFlowStep1';
import ProductAddFlowStep2 from '../../../components/delivery/ProductAddFlowStep2';
import ProductAddFlowStep3 from '../../../components/delivery/ProductAddFlowStep3';
import type { Step3FormValues } from '../../../components/delivery/ProductAddFlowStep3';
import { useGetInactiveProductsByVesselQuery } from '../../../store/apiSlices/delivery/deliveryApis';
import {
  useAddProductToVesselMutation,
  useGetActiveProductsBySupplierQuery,
  useGetAllProductsNotInVesselQuery,
  useUpdateVesselProductStatusMutation,
} from '../../../store/apiSlices/lubricationChart/lubricationChartsApis';
import { useCreateTypeInProductForVesselMutation } from '../../../store/apiSlices/typeInProducts/typeInProductsApis';
import { useSelector } from 'react-redux';

interface Filters {
  id: string;
  productName: string;
  maker: string;
  category: string;
  type: string;
  unit: string;
  status: string;
}

interface ProductAddFlowContainerProps {
  open: boolean;
  vesselId?: number;
  vesselName?: string;
  supplierId?: number;
  onClose: () => void;
  deliveryType?: string;
  refetchProductsWithSupplier?: () => void;
  refetchProductsWithoutSupplier?: () => void;
  supplierIdForMakerList?: number | null;
}

const ProductAddFlowContainer: React.FC<ProductAddFlowContainerProps> = ({
  open,
  vesselId,
  vesselName,
  supplierId,
  onClose,
  deliveryType,
  refetchProductsWithSupplier,
  refetchProductsWithoutSupplier,
  supplierIdForMakerList: _supplierIdForMakerList,
}) => {
  const { t } = useTranslation();
  const userInfo = useSelector((state: any) => state.user.userInfo);
  const [currentStep, setCurrentStep] = useState<number>(1);

  // State for Step 1 (Reactivate)
  const [step1SelectedProducts, setStep1SelectedProducts] = useState<number[]>([]);
  const [step1Filters] = useState<Filters>({
    id: '',
    productName: '',
    maker: '',
    category: '',
    type: '',
    unit: '',
    status: '',
  });

  // State for Step 2 (Master Catalogue)
  const [step2SelectedProducts, setStep2SelectedProducts] = useState<number[]>([]);
  const [step2Filters] = useState<Filters>({
    id: '',
    productName: '',
    maker: '',
    category: '',
    type: '',
    unit: '',
    status: '',
  });

  // Form for Step 3 (Type-in Product)
  const {
    control: step3Control,
    handleSubmit: handleStep3FormSubmit,
    reset: resetStep3Form,
    formState: { errors: step3Errors, isValid: isStep3FormValid },
  } = useForm<Step3FormValues>({
    mode: 'onChange',
    defaultValues: {
      productName: '',
      maker: '',
      category: '',
      type: '',
      unit: '',
      status: 'Active',
    },
  });

  // API hooks to get real data - following ProductListingContainer pattern
  const {
    data: inactiveProducts = [],
    isLoading: isLoadingInactiveProducts,
    refetch: refetchInactiveProducts,
  } = useGetInactiveProductsByVesselQuery(
    { vesselId: vesselId || 0 },
    { skip: !open || !vesselId }, // Call when modal is open and vesselId exists
  );

  // Determine which API to use for active products based on delivery type and supplierId
  const useSupplierAPI = Boolean(
    (deliveryType === 'standard' || deliveryType === 'newBuilding') && supplierId && supplierId > 0,
  );

  // API hook for active products by supplier (for standard and new building with supplier)
  const {
    data: activeProductsBySupplier = [],
    isLoading: isLoadingActiveProductsBySupplier,
    refetch: refetchActiveProductsBySupplier,
  } = useGetActiveProductsBySupplierQuery(
    { supplierId: supplierId || 0, vesselId: vesselId },
    { skip: !open || !useSupplierAPI }, // Call when modal is open and we should use supplier API
  );

  // API hook for active products not in vessel (for other delivery types)
  const {
    data: activeProductsByVessel = [],
    isLoading: isLoadingActiveProductsByVessel,
    refetch: refetchActiveProductsByVessel,
  } = useGetAllProductsNotInVesselQuery(
    vesselId || 0,
    { skip: !open || !vesselId || useSupplierAPI }, // Call when modal is open, vesselId exists, and we're NOT using supplier API
  );

  // Combine the results based on which API we're using
  const activeProducts = useSupplierAPI ? activeProductsBySupplier : activeProductsByVessel;
  const isLoadingActiveProducts = useSupplierAPI
    ? isLoadingActiveProductsBySupplier
    : isLoadingActiveProductsByVessel;
  const refetchActiveProducts = useSupplierAPI
    ? refetchActiveProductsBySupplier
    : refetchActiveProductsByVessel;

  // Mutation for updating vessel product status
  const [updateVesselProductStatus] = useUpdateVesselProductStatusMutation();

  // Mutation for adding products to vessel
  const [addProductToVessel] = useAddProductToVesselMutation();

  // Mutation for creating type-in product
  const [createTypeInProductForVessel] = useCreateTypeInProductForVesselMutation();

  // Force refetch APIs when modal opens or step changes
  useEffect(() => {
    if (!open || !vesselId) return;

    if (currentStep === 1) {
      // Step 1: Fetch inactive products
      refetchInactiveProducts();
    } else if (currentStep === 2) {
      // Step 2: Fetch active products
      refetchActiveProducts();
    }
  }, [open, currentStep, vesselId, refetchInactiveProducts, refetchActiveProducts]);

  const groupsOptions = useMemo(() => {
    return [] as string[];
  }, []);

  // Transform API data to display format (similar to LubricationDetailContainer)
  const masterCatalogueList = useMemo(() => {
    // Check if activeProducts exists and has the expected structure
    if (!activeProducts || Array.isArray(activeProducts) || !activeProducts.data?.content) {
      return [];
    }

    return activeProducts.data.content.map((product: any) => {
      return {
        id: product.id,
        productName: product.name,
        maker: product.makerName || '-',
        category: product.categoryName || '-',
        type: product.typeName || '-',
        unit: product.symbol || '-',
        status: product.active ? 'Active' : 'Inactive',
      };
    });
  }, [activeProducts]);

  // Create lubrication data structure
  const lubricationData = useMemo(
    () => ({
      inactiveProducts, // Already parsed by InactiveProductsByVesselParser
      masterCatalogue: masterCatalogueList, // Use transformed data instead of raw activeProducts
      groups: groupsOptions,
    }),
    [inactiveProducts, masterCatalogueList, groupsOptions],
  );

  const isLoading = isLoadingInactiveProducts || isLoadingActiveProducts;
  const error = false; // We can add error handling later if needed

  const handleStepChange = useCallback((step: number): void => {
    setCurrentStep(step);
  }, []);

  const handleBack = useCallback((): void => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      onClose();
    }
  }, [currentStep, onClose]);

  const handleClose = useCallback((): void => {
    onClose();
    setCurrentStep(1); // Reset step when closing
    // Reset all state when closing
    setStep1SelectedProducts([]);
    setStep2SelectedProducts([]);
    resetStep3Form();
  }, [onClose, resetStep3Form]);

  // Step 1 handlers
  const handleStep1ProductSelect = useCallback((productId: number): void => {
    setStep1SelectedProducts((prev) =>
      prev.includes(productId) ? prev.filter((id) => id !== productId) : [...prev, productId],
    );
  }, []);

  const handleStep1ActivateProducts = useCallback(async (): Promise<void> => {
    if (!lubricationData || !vesselId) return;

    const productsToActivate = lubricationData.inactiveProducts.filter((product: any) =>
      step1SelectedProducts.includes(product.id),
    );

    if (productsToActivate.length === 0) {
      showErrorToast(t('toast.validationError'), t('productAddFlow.validation.noProductsSelected'));
      return;
    }

    try {
      // Call API to update product status from inactive to active - use .unwrap() to throw error on failure
      const updatePromises = productsToActivate.map(
        (product: any) =>
          updateVesselProductStatus({
            vesselId: vesselId,
            productId: product.id,
            active: true,
            // updatedBy: 1 // You may want to get this from user context or props
          }).unwrap(), // This will throw an error if any API call fails
      );

      await Promise.all(updatePromises);

      // Refetch inactive products to update the list
      await refetchInactiveProducts();
      if (productsToActivate?.length > 1) {
        showSuccessToast(t('productAddFlow.success.activatedPlural'));
      } else {
        showSuccessToast(t('productAddFlow.success.activated'));
      }

      // Refetch delivery products based on delivery type
      if (deliveryType === 'standard' || deliveryType === 'newBuilding') {
        // For standard and new building, refetch products by vessel and supplier
        if (refetchProductsWithSupplier) {
          await refetchProductsWithSupplier();
        }
      } else if (deliveryType === 'adjusting' || deliveryType === 'secondHand') {
        // For adjusting and 2nd hand, refetch products by vessel only
        if (refetchProductsWithoutSupplier) {
          await refetchProductsWithoutSupplier();
        }
      }

      handleClose();
    } catch (error: any) {
      logger.error('Failed to activate products', error as Error);
      // Don't add products to local table if API failed
      // Don't close modal so user can retry
    }
  }, [
    lubricationData,
    step1SelectedProducts,
    vesselId,
    updateVesselProductStatus,
    refetchInactiveProducts,
    deliveryType,
    refetchProductsWithSupplier,
    refetchProductsWithoutSupplier,
    handleClose,
    t,
  ]);

  // Step 2 handlers
  const handleStep2ProductSelect = useCallback((productId: number): void => {
    setStep2SelectedProducts((prev) =>
      prev.includes(productId) ? prev.filter((id) => id !== productId) : [...prev, productId],
    );
  }, []);

  const handleStep2AddToLubricationChart = useCallback(async (): Promise<void> => {
    if (!lubricationData || !vesselId) return;

    const productsToAdd = lubricationData.masterCatalogue.filter((product: any) =>
      step2SelectedProducts.includes(product.id),
    );

    if (productsToAdd.length === 0) {
      showErrorToast(
        t('toast.validationError'),
        t('productAddFlow.validation.noProductsSelectedToAdd'),
      );
      return;
    }

    try {
      // Call API to add products to vessel - use .unwrap() to throw error on failure
      await addProductToVessel({
        vesselId: vesselId,
        products: productsToAdd.map((product: any) => ({
          productId: product.id,
          active: true,
        })),
      }).unwrap();

      // Refetch master catalogue to get updated data
      await refetchActiveProducts();

      // Refetch delivery products based on delivery type
      if (deliveryType === 'standard' || deliveryType === 'newBuilding') {
        // For standard and new building, refetch products by vessel and supplier
        if (refetchProductsWithSupplier) {
          await refetchProductsWithSupplier();
        }
      } else if (deliveryType === 'adjusting' || deliveryType === 'secondHand') {
        // For adjusting and 2nd hand, refetch products by vessel only
        if (refetchProductsWithoutSupplier) {
          await refetchProductsWithoutSupplier();
        }
      }

      handleClose();
    } catch (error: any) {
      logger.error('Failed to add products to vessel', error as Error);
      // Don't close modal so user can retry
    }
  }, [
    lubricationData,
    step2SelectedProducts,
    vesselId,
    addProductToVessel,
    refetchActiveProducts,
    deliveryType,
    refetchProductsWithSupplier,
    refetchProductsWithoutSupplier,
    handleClose,
    t,
  ]);

  // Step 3 handler
  const handleStep3Submit = useCallback(
    async (values: Step3FormValues): Promise<void> => {
      try {
        // Call API to create type-in product - use .unwrap() to throw error on failure
        const response = await createTypeInProductForVessel({
          name: values.productName,
          makerId: values.maker ? Number(values.maker) : undefined,
          categoryId: values.category ? Number(values.category) : undefined,
          typeId: values.type ? Number(values.type) : undefined,
          uomId: values.unit ? Number(values.unit) : undefined,
          vesselId: vesselId,
          typeInManagerId: userInfo?.userId,
          active: true,
        }).unwrap();

        // Refetch API to get updated data
        await refetchActiveProducts();

        showSuccessToast(response?.message);

        // Refetch delivery products based on delivery type
        if (deliveryType === 'standard' || deliveryType === 'newBuilding') {
          // For standard and new building, refetch products by vessel and supplier
          if (refetchProductsWithSupplier) {
            await refetchProductsWithSupplier();
          }
        } else if (deliveryType === 'adjusting' || deliveryType === 'secondHand') {
          // For adjusting and 2nd hand, refetch products by vessel only
          if (refetchProductsWithoutSupplier) {
            await refetchProductsWithoutSupplier();
          }
        }

        handleClose();
      } catch (error: any) {
        logger.error('Failed to create type-in product', error as Error);
      }
    },
    [
      vesselId,
      createTypeInProductForVessel,
      refetchActiveProducts,
      deliveryType,
      refetchProductsWithSupplier,
      refetchProductsWithoutSupplier,
      handleClose,
      userInfo?.userId,
    ],
  );

  // Computed values
  const step1FilteredProducts =
    lubricationData?.inactiveProducts?.filter((product: any) => {
      return (
        product.id.toString().includes(step1Filters.id) &&
        product.productName.toLowerCase().includes(step1Filters.productName.toLowerCase()) &&
        product.maker.toLowerCase().includes(step1Filters.maker.toLowerCase()) &&
        product.category.toLowerCase().includes(step1Filters.category.toLowerCase()) &&
        product.type.toLowerCase().includes(step1Filters.type.toLowerCase()) &&
        product.unit.toLowerCase().includes(step1Filters.unit.toLowerCase()) &&
        product.status.toLowerCase().includes(step1Filters.status.toLowerCase())
      );
    }) || [];

  const step2FilteredProducts =
    lubricationData?.masterCatalogue?.filter((product: any) => {
      return (
        product.id.toString().includes(step2Filters.id) &&
        product.productName.toLowerCase().includes(step2Filters.productName.toLowerCase()) &&
        product.maker.toLowerCase().includes(step2Filters.maker.toLowerCase()) &&
        product.category.toLowerCase().includes(step2Filters.category.toLowerCase()) &&
        product.type.toLowerCase().includes(step2Filters.type.toLowerCase()) &&
        product.unit.toLowerCase().includes(step2Filters.unit.toLowerCase()) &&
        product.status.toLowerCase().includes(step2Filters.status.toLowerCase())
      );
    }) || [];

  const step1HasSelectedProducts = step1SelectedProducts.length > 0;
  const step2HasSelectedProducts = step2SelectedProducts.length > 0;

  // Don't render if modal is not open or data is loading
  if (!open || isLoading) return null;

  // Don't render if there's an error or no data
  if (error || !lubricationData) return null;

  return (
    <>
      {currentStep === 1 && (
        <ProductAddFlowStep1
          onNext={handleStepChange}
          onClose={handleClose}
          onActivateProducts={handleStep1ActivateProducts}
          selectedProducts={step1SelectedProducts}
          onProductSelect={handleStep1ProductSelect}
          hasSelectedProducts={step1HasSelectedProducts}
          filteredProducts={step1FilteredProducts}
          vesselName={vesselName}
        />
      )}
      {currentStep === 2 && (
        <ProductAddFlowStep2
          onNext={handleStepChange}
          onBack={handleBack}
          onClose={handleClose}
          onAddToLubricationChart={handleStep2AddToLubricationChart}
          selectedProducts={step2SelectedProducts}
          onProductSelect={handleStep2ProductSelect}
          hasSelectedProducts={step2HasSelectedProducts}
          filteredProducts={step2FilteredProducts}
        />
      )}
      {currentStep === 3 && (
        <ProductAddFlowStep3
          onBack={handleBack}
          onClose={handleClose}
          onSubmit={handleStep3FormSubmit(handleStep3Submit)}
          control={step3Control}
          errors={step3Errors}
          isFormValid={isStep3FormValid}
        />
      )}
    </>
  );
};

export default ProductAddFlowContainer;
